import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import { formatDate } from "../../utils/dateUtils";
import { encrypt } from "../../utils/encryption";
import { getCurrencySymbol } from "../../utils/currency";

function PurchasePaymentMasterPage() {
  const navigate = useNavigate();
  const basePath = "/purchase-payment-master";

  // Main CRUD Hook for Purchase Master Payment Summaries
  const endpoints = useMemo(
    () => ({
      list: "/purchase-payment-master",
      create: "/purchase-payment-master",
      edit: (id) => `/purchase-payment-master/${id}`,
      update: (id) => `/purchase-payment-master/${id}`,
      delete: (id) => `/purchase-payment-master/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    changePage,
    changeSort,
    updateFilters,
  } = useCrud({
    endpoints,
    idKey: "pmId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  // Navigate to Manage Payments Page
  const handleManagePayments = (item) => {
    navigate(`${basePath}/${encrypt(item.pmId)}/payments`, { state: { pm: item } });
  };

  // Table Columns Definition
  const tableColumns = useMemo(
    () => [
      {
        key: "billNo",
        label: "Bill No",
        sortable: true,
        className: "font-bold text-slate-800 dark:text-white",
        render: (item) => String(item?.billNo || "N/A"),
      },
      {
        key: "billDate",
        label: "Bill Date",
        sortable: true,
        render: (item) => formatDate(item?.billDate),
      },
      {
        key: "vendorName",
        label: "Vendor",
        // sortable: true,
        render: (item) => item?.vendorName || "N/A",
      },
      {
        key: "totalPaymentAmount",
        label: "Total Payable",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => {
          const currencySymbol = item?.currency?.symbol || getCurrencySymbol(item?.currency?.currencyCode) || "$";
          return (
            <div className="flex flex-col items-end">
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {currencySymbol}{Number(item?.totalPaymentAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              <span className="text-[10px] text-slate-400">
                ₹{Number(item?.totalPaymentAmountinRs || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          );
        },
      },
      {
        key: "paidAmount",
        label: "Paid Amount",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => {
          const currencySymbol = item?.currency?.symbol || getCurrencySymbol(item?.currency?.currencyCode) || "$";
          return (
            <div className="flex flex-col items-end">
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                {currencySymbol}{Number(item?.paidAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              <span className="text-[10px] text-slate-400">
                ₹{Number(item?.paidAmountinRs || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          );
        },
      },
      {
        key: "remainingAmount",
        label: "Remaining",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => {
          const currencySymbol = item?.currency?.symbol || getCurrencySymbol(item?.currency?.currencyCode) || "$";
          return (
            <div className="flex flex-col items-end">
              <span className={`font-bold ${Number(item?.remainingAmount || 0) <= 0 ? "text-slate-400" : "text-amber-600"}`}>
                {currencySymbol}{Number(item?.remainingAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              <span className="text-[10px] text-slate-400">
                ₹{Number(item?.remainingAmountinRs || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        headerClassName: "text-center",
        className: "text-center",
        render: (item) => {
          const remaining = Number(item?.remainingAmount || 0);
          const paid = Number(item?.paidAmount || 0);

          let badgeClass = "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
          let statusText = "Unpaid";

          if (remaining <= 0) {
            badgeClass = "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400";
            statusText = "Fully Paid";
          } else if (paid > 0) {
            badgeClass = "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400";
            statusText = "Partially Paid";
          }

          return (
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${badgeClass}`}>
              {statusText}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      {/* <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <h1 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
          Purchase <span className="text-primary-600">Payments</span>
        </h1>
      </div> */}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        renderActions={(item) => {
          const isFullyPaid = Number(item?.remainingAmount || 0) <= 0;
          return (
            <Button
              variant="secondary"
              size="sm"
              className={`flex items-center gap-1 h-8 px-2.5 text-xs font-bold uppercase tracking-wider transition-all active:scale-95 ${isFullyPaid
                  ? "border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:text-slate-400"
                  : "border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300"
                }`}
              onClick={() => handleManagePayments(item)}
              title={isFullyPaid ? "View Payments" : "Manage Payments"}
            >
              <i className="material-icons text-sm">{isFullyPaid ? "visibility" : "payments"}</i>
              {isFullyPaid ? "View" : "Pay"}
            </Button>
          );
        }}
        showActions={true}
        searchKey="search"
        searchPlaceholder="Search by Bill No or Vendor..."
        tableScrollHeight="58vh"
      />
    </div>
  );
}

export default PurchasePaymentMasterPage;
